/**
 * Profile Data Transformation Utility
 *
 * Transforms the flat customer data structure encrypted by the consultant
 * into the nested CustomerProfile structure expected by the customer frontend.
 *
 * Consultant encrypts: flat structure with email, phone, address as top-level fields
 * Customer expects: nested structure with contact.email, contact.phone, contact.address
 */

import type {
  CustomerProfile,
  CommunicationPreferences,
  CommunicationMethod,
  BeautyProfile,
  SkinType,
  SkinBlemishes,
  ImprovementGoal,
  TopicOfInterest,
  ContactTime,
} from '@/types/profile'

/**
 * Raw decrypted customer data from consultant's encryption
 * This is the flat structure the consultant frontend uses
 */
export interface DecryptedCustomerData {
  id: string
  userId: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  address?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  birthday?: string
  occupation?: string
  referredBy?: string
  preferredContactMethods?: string[]
  bestTimeToContact?: string
  skinProfile?: {
    skinType?: string | string[] // Can be single value or array
    blemishes?: string | string[] // Can be single value or array
    whatToChange?: string
    improvementGoals?: string[]
    sensitivityLevel?: number // 1-10 scale
    currentRoutine?: string[]
    routineOther?: string
    topicsOfInterest?: string[]
  }
  notes?: string
  tags?: string[]
  createdAt: string
  updatedAt: string
  // Invitation fields (not needed for profile display)
  invitationEmail?: string
  invitationStatus?: string
  invitationSentAt?: string
  invitationToken?: string
}

/**
 * API response structure for communication preferences
 * This is the non-encrypted data from the backend
 */
export interface ApiCommunicationPreferences {
  emailNotifications?: boolean
  smsNotifications?: boolean
  appointmentReminders?: boolean
  orderUpdates?: boolean
  marketingEmails?: boolean
}

/**
 * Full profile API response (before encryption extraction)
 */
export interface ProfileApiResponse {
  customerId: string
  consultantId: string
  ciphertext: string
  iv: string
  authTag?: string
  version: number
  language: string
  timezone: string
  communicationPreferences: ApiCommunicationPreferences
  createdAt: string
  updatedAt: string
  dekId?: string
}

/**
 * Transform API communication preferences to the expected format
 * Uses decrypted data for contact methods and times when available
 */
function transformPreferences(
  apiPrefs: ApiCommunicationPreferences,
  decrypted: DecryptedCustomerData
): CommunicationPreferences {
  // Use decrypted preferredContactMethods if available
  const preferredMethods = decrypted.preferredContactMethods?.map(
    (m) => m as CommunicationMethod
  )
  const preferredMethod: CommunicationMethod =
    (preferredMethods?.[0] as CommunicationMethod) || 'email'

  // Use decrypted bestTimeToContact if available
  const preferredContactTime: ContactTime =
    (decrypted.bestTimeToContact as ContactTime) || 'anytime'

  return {
    preferredMethod,
    preferredMethods,
    allowPromotionalEmails: apiPrefs.marketingEmails ?? false,
    allowSmsNotifications: apiPrefs.smsNotifications ?? false,
    allowAppointmentReminders: apiPrefs.appointmentReminders ?? true,
    preferredLanguage: 'en', // Default
    preferredContactTime,
  }
}

/**
 * Transform skinProfile to beautyProfile format
 * Keeps all data from consultant's structure
 */
function transformSkinProfile(
  skinProfile?: DecryptedCustomerData['skinProfile']
): BeautyProfile | undefined {
  if (!skinProfile) return undefined

  // Keep skinType as array (don't flatten to single value)
  const skinTypeArray = skinProfile.skinType
    ? (Array.isArray(skinProfile.skinType)
        ? skinProfile.skinType
        : [skinProfile.skinType])
    : undefined

  // Keep blemishes as separate field (don't map to skinConcerns)
  const blemishesArray = skinProfile.blemishes
    ? (Array.isArray(skinProfile.blemishes)
        ? skinProfile.blemishes
        : [skinProfile.blemishes])
    : undefined

  return {
    skinType: skinTypeArray as SkinType[] | undefined,
    blemishes: blemishesArray as SkinBlemishes[] | undefined,
    skinConcerns: undefined, // Not used by consultant
    allergies: undefined,
    notes: skinProfile.whatToChange,
    // Pass through additional fields from consultant
    improvementGoals: skinProfile.improvementGoals as ImprovementGoal[] | undefined,
    sensitivityLevel: skinProfile.sensitivityLevel,
    currentRoutine: skinProfile.currentRoutine,
    topicsOfInterest: skinProfile.topicsOfInterest as TopicOfInterest[] | undefined,
  }
}

/**
 * Transform decrypted flat customer data to CustomerProfile format
 *
 * @param decrypted - The decrypted flat customer data from consultant's encryption
 * @param apiResponse - The full API response containing non-encrypted fields
 * @param consultantName - Optional consultant name (may come from elsewhere)
 * @param consultantWebsite - Optional consultant website URL (may come from elsewhere)
 * @returns CustomerProfile in the expected nested format
 */
export function transformDecryptedProfile(
  decrypted: DecryptedCustomerData,
  apiResponse: ProfileApiResponse,
  consultantName?: string,
  consultantWebsite?: string
): CustomerProfile {
  // Build address object only if we have address data
  const hasAddressData =
    decrypted.address ||
    decrypted.city ||
    decrypted.state ||
    decrypted.zipCode ||
    decrypted.country

  return {
    id: apiResponse.customerId,
    customerId: apiResponse.customerId,
    consultantId: apiResponse.consultantId,
    consultantName: consultantName || 'Your Consultant',
    consultantWebsite: consultantWebsite,

    // Personal info
    firstName: decrypted.firstName,
    lastName: decrypted.lastName,
    dateOfBirth: decrypted.birthday,

    // Transform flat contact fields to nested structure
    contact: {
      email: decrypted.email || '',
      phone: decrypted.phone,
      address: hasAddressData
        ? {
            street: decrypted.address || '',
            city: decrypted.city || '',
            state: decrypted.state || '',
            postalCode: decrypted.zipCode || '',
            country: decrypted.country || '',
          }
        : undefined,
    },

    // Map skinProfile to beautyProfile
    beautyProfile: transformSkinProfile(decrypted.skinProfile),

    // Use API communicationPreferences + decrypted contact preferences
    preferences: transformPreferences(apiResponse.communicationPreferences, decrypted),

    // Metadata
    createdAt: apiResponse.createdAt,
    updatedAt: apiResponse.updatedAt,
  }
}
